// The device mark of the drill board (arcade.md §17.8, R1931).
//
// A drill leaves no record (arcade.md §17.1), so no server can say which
// drills a player finished. This device can: the page writes one mark when
// the drill end card says COMPLETE, and the DRILLS rows read the marks and
// say ✓ DONE HERE. The mark names no account and reaches no server, so a
// second device starts clean, and the row says DONE HERE and not DONE.
//
// The key is the drill's folder and the map slug, with no revision: a new
// revision of a drill map is the same drill, and it keeps its mark. A store
// that refuses (a private window, a full store) has no memory, and the
// board then draws no mark.

import { readStore, writeStore } from '../ui/localstore';

/** The local storage key: a JSON list of `<folder>/<slug>`. */
export const DRILL_MARKS_KEY = 'arcade.drillsDone';

/** The key of one drill: `<folder>/<slug>`, with the `-v<revision>` of
 *  the map id cut. */
export function drillMarkKey(category: string, mapId: string): string {
  return `${category}/${mapId.replace(/-v\d+$/, '')}`;
}

/** The marks this device holds. A missing or broken value is no mark. */
export function readDrillMarks(): Set<string> {
  const raw = readStore(DRILL_MARKS_KEY);
  if (raw === null) return new Set();
  try {
    const list: unknown = JSON.parse(raw);
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/** True where this device finished the drill. */
export function isDrillDone(marks: ReadonlySet<string>, category: string, mapId: string): boolean {
  return marks.has(drillMarkKey(category, mapId));
}

/**
 * Writes the mark of one drill that this device finished. The page calls
 * it when the drill end card says COMPLETE; an UNFINISHED end writes none.
 * Two empty fields name no drill, and the call writes nothing.
 */
export function markDrillDone(category: string, mapId: string): void {
  if (category === '' || mapId === '') return;
  const marks = readDrillMarks();
  const key = drillMarkKey(category, mapId);
  if (marks.has(key)) return;
  marks.add(key);
  writeStore(DRILL_MARKS_KEY, JSON.stringify([...marks].sort()));
}

// ---- the drill pass (R2119) ----------------------------------------------
//
// PLAY NOW of the landing's ARCADE console opens a full match, and a new
// player who presses it has never seen a drill. So the landing sends a
// device with no drill pass to `/arcade?drill=1` first, and the arcade page
// writes the pass when a drill ends, with any verdict, or when the player
// presses SKIP THE DRILL. A device that finished a drill before the pass
// existed holds a drill mark, and a drill mark counts as the pass. A store
// that refuses holds no pass, so that device goes to the drill every time,
// with SKIP THE DRILL on its first step.

/** The local storage key of the drill pass: "1" once this device ended a
 *  drill or skipped it. */
export const DRILL_PASS_KEY = 'ap.arcade.drillPass';

/** True where this device holds the drill pass. */
export function hasDrillPass(): boolean {
  return readStore(DRILL_PASS_KEY) === '1' || readDrillMarks().size > 0;
}

/** Writes the drill pass. A store that refuses forgets it. */
export function writeDrillPass(): void {
  writeStore(DRILL_PASS_KEY, '1');
}

/** The address PLAY NOW of the ARCADE console opens: the first drill for a
 *  device with no drill pass, the quick match for every other device. */
export function arcadePlayHref(): string {
  return hasDrillPass() ? '/arcade?quick=1' : '/arcade?drill=1';
}
